import React from 'react'
import { Badge, Box, HStack, SimpleGrid, Text } from '@chakra-ui/react'
import { listedRentLabel, readinessDate } from 'lib/vacancy-readiness'
import type { ReadinessDetail } from 'lib/vacancy-readiness'
import type { RentPriceSummary } from './RentPricePanel'

export default function VacancySummary({
  detail,
  price,
  userId
}: {
  detail: ReadinessDetail
  price: RentPriceSummary | null
  userId?: string
}) {
  const currentPriceSummary =
    price?.vacancyPeriodId === detail.vacancy_period_id && price.userId === userId ? price : null
  return (
    <SimpleGrid
      columns={{ base: 1, md: currentPriceSummary?.visible === false ? 2 : 3 }}
      spacing={3}
      mt={4}
      aria-label='Vacancy summary'
    >
      {currentPriceSummary?.visible !== false && (
        <Box p={3} bg='gray.50' borderWidth={1} borderRadius='md'>
          <Text fontSize='sm' color='gray.600'>
            Listed rent
          </Text>
          <Text fontSize='lg' fontWeight='semibold'>
            {!currentPriceSummary || currentPriceSummary.loading
              ? 'Loading…'
              : currentPriceSummary.unavailable && !currentPriceSummary.listedRent
              ? 'Unavailable'
              : listedRentLabel(currentPriceSummary.listedRent)}
          </Text>
          <Text fontSize='xs' color='gray.600'>
            {currentPriceSummary?.unavailable ? 'Could not refresh price' : 'per month'}
          </Text>
        </Box>
      )}
      {(
        [
          [
            'Tour ready date',
            detail.current_confirmed_tour_ready_date,
            detail.estimated_tour_ready_date,
            detail.readiness_state
          ],
          [
            'Rent ready date',
            detail.current_confirmed_rent_ready_date,
            detail.expected_move_in_date,
            detail.rent_readiness_state
          ]
        ] as const
      ).map(([label, actual, estimate, state]) => (
        <Box
          key={label}
          p={3}
          bg='teal.50'
          borderWidth={1}
          borderColor='teal.100'
          borderRadius='md'
        >
          <Text fontSize='sm' color='gray.600'>
            {label}
          </Text>
          <Text fontSize='lg' fontWeight='semibold'>
            {actual || estimate ? readinessDate(actual || estimate) : 'Not entered'}
          </Text>
          <HStack spacing={1} flexWrap='wrap'>
            <Badge colorScheme={actual ? 'green' : 'gray'} fontSize='10px'>
              {actual ? 'Actual' : estimate ? 'Estimate' : 'Not confirmed'}
            </Badge>
            {state === 'not_ready' && (
              <Badge colorScheme='orange' fontSize='10px'>
                Not ready
              </Badge>
            )}
          </HStack>
        </Box>
      ))}
    </SimpleGrid>
  )
}
